// BURN (owner 2026-11-02, fire skill family): ONE shared burn status for every fire source that hurts foes. IP-NEUTRAL.
// Pure + deterministic: no three, no DOM, no Rng. The Sim owns the per-tick loop (Sim.updateBurns); this file holds the
// rules so they can be unit-tested on their own.
//
// Sources that set it: Fire Breath, Flame Ring (+ its lingering circle), Wyrmstorm, the rank-5 fire pops, Flare Barrage's
// mini fireballs (a light ember), Scorched Earth (foes standing in burning grass) and Pyre Sight's burning lines.
// Map hazards (lava, fire vents) keep their own direct damage; Kindling scales that too.
//
// Rules
//  - A burn is a damage rate (dps, already including the hero's damage multiplier when it was applied) and seconds left.
//  - It never stacks. A fresh burn keeps the HIGHER rate and the LARGER remaining damage pool (rate × seconds): a weak burn
//    can refresh a strong one only as far as its own total damage reaches, so mixing sources is always bounded by the
//    strongest single application.
//  - Damage lands every BURN_TICK ticks (staggered by foe id so hundreds of burning foes don't all tick on one frame), never
//    crits and seeps through Bulwark plating (fire is not a 'small hit').
//  - Spread (Fire Breath rank 3+): a burning foe sets the foes it is touching alight at BURN_SPREAD_K of its rate for
//    BURN_SPREAD_DUR of its time left, at most BURN_SPREAD_N per check, and only BURN_GEN_MAX hops away from the first
//    burn, so a crowd can't relight itself forever.

/** ticks between burn damage ticks (0.25 s) */
export const BURN_TICK = 15;
/** ticks between spread checks; foes lit per burning foe per check; rate / time share a spread burn gets; max hops */
export const BURN_SPREAD_GAP = 20, BURN_SPREAD_N = 2, BURN_SPREAD_K = 0.6, BURN_SPREAD_DUR = 0.6, BURN_GEN_MAX = 2;
/** hard cap on new spread burns per check (whole field) */
export const BURN_SPREAD_CAP = 48;
/** how close two foes must be to pass the burn on (m, added to both body radii) */
export const BURN_TOUCH = 0.35;
/** hit colour of burn ticks (render: no impact spark for these; the burning-foe flames carry the look) */
export const BURN_COLOR = 0xff6a1a;

/** the per-foe burn state (Sim.Enemy carries these fields) */
export interface Burnable { burnT: number; burnDps: number; burnSrc: string; burnGen: number }

/** Merge a new burn (rate dps for dur seconds, from src, spread generation gen) into a foe's burn. Returns true if the foe
 *  was not burning before. */
export function mergeBurn(e: Burnable, dps: number, dur: number, src: string, gen: number): boolean {
  if (!(dps > 0) || !(dur > 0)) return false;
  const fresh = e.burnT <= 0;
  if (fresh) { e.burnT = dur; e.burnDps = dps; e.burnSrc = src; e.burnGen = gen; return true; }
  const pool = e.burnDps * e.burnT, pool2 = dps * dur;
  if (dps >= e.burnDps) {
    e.burnDps = dps; e.burnT = Math.max(pool, pool2) / dps;
    if (pool2 >= pool) { e.burnSrc = src; e.burnGen = gen; } else e.burnGen = Math.min(e.burnGen, gen);
  } else if (pool2 > pool) {
    e.burnT = pool2 / e.burnDps; e.burnGen = Math.min(e.burnGen, gen);
  }
  return false;
}

/** Cone test shared by Fire Breath (sim) and its tests: is a foe of body radius er at (ex, ez) inside the cone from (px, pz)
 *  along unit (dx, dz), half-angle half (rad), reach range (m)? Its body counts: the edge of the foe may poke in. Anything
 *  within `touch` m of the origin is always inside (point-blank). */
export function inCone(px: number, pz: number, dx: number, dz: number, half: number, range: number, ex: number, ez: number, er: number, touch = 0.8): boolean {
  const rx = ex - px, rz = ez - pz, d = Math.hypot(rx, rz);
  if (d - er > range) return false;
  if (d <= touch) return true;
  const cosA = (rx * dx + rz * dz) / d;
  const slack = Math.asin(Math.min(1, er / d)); // the foe's body widens the angle it can be caught at
  return cosA >= Math.cos(Math.min(Math.PI, half + slack));
}
